import React, { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { SiteHeader } from './header'

interface PostSummary {
  date: string;
  slug: string;
  title: string;
}

interface Post extends PostSummary {
  body: string;
}

function formatPostDate(stored: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(stored)
  if (!m) return stored
  const [, y, mo, d] = m
  const date = new Date(Number(y), Number(mo) - 1, Number(d))
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

interface PostDetailProps {
  post: Post;
  onBack: () => void;
}

function PostDetail({ post, onBack }: PostDetailProps) {
  return (
    <div>
      <button className="btn btn-link ps-0 mb-3" onClick={onBack}>
        ← Back to posts
      </button>
      <h2>{post.title}</h2>
      <p className="text-muted small">{formatPostDate(post.date)}</p>
      <hr />
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body}</ReactMarkdown>
    </div>
  )
}

interface PostListProps {
  posts: PostSummary[];
  onSelect: (post: PostSummary) => void;
}

function PostList({ posts, onSelect }: PostListProps) {
  if (posts.length === 0) {
    return <p className="text-muted">No posts yet.</p>
  }
  return (
    <ul className="list-group list-group-flush">
      {posts.map((post) => (
        <li key={post.slug} className="list-group-item px-0">
          <a
            href="/#"
            className="text-decoration-none"
            onClick={(e) => { e.preventDefault(); onSelect(post); }}
          >
            <span className="fw-semibold">{post.title}</span>
            <span className="text-muted small ms-2">{formatPostDate(post.date)}</span>
          </a>
        </li>
      ))}
    </ul>
  )
}

export function Spinner() {
  return (
    <div className="d-flex justify-content-center my-4">
      <div className="spinner-border" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  )
}

export function PostsPage() {
  const [summaries, setSummaries] = useState<PostSummary[] | null>(null)
  const [selected, setSelected] = useState<Post | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetch('/posts/index.json')
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data: PostSummary[]) => setSummaries(data))
      .catch((e) => setError(String(e)))
  }, [])

  const onSelect = (summary: PostSummary) => {
    setLoadingDetail(true)
    setError(null)
    fetch(`/posts/${summary.date}.json`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((post: Post) => {
        setSelected(post)
        setLoadingDetail(false)
      })
      .catch((e) => {
        setError(String(e))
        setLoadingDetail(false)
      })
  }

  let content: React.ReactNode
  if (error) {
    content = <div className="alert alert-danger">Failed to load posts: {error}</div>
  } else if (loadingDetail) {
    content = <Spinner />
  } else if (selected) {
    content = <PostDetail post={selected} onBack={() => setSelected(null)} />
  } else if (summaries === null) {
    content = <Spinner />
  } else {
    content = <PostList posts={summaries} onSelect={onSelect} />
  }

  return (
    <div className="container-md">
      <SiteHeader title="Posts" />
      {content}
    </div>
  )
}
